import React, { useState, useRef, useEffect, useCallback } from 'react';
import { cn } from '@/lib/utils';

interface AudioMessageProps {
    audioUrl: string;
    duration?: number;
    className?: string;
    isOwn?: boolean; // If true, this is the sender's message (blue background)
}

// Format seconds to MM:SS
const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export function AudioMessage({
    audioUrl,
    duration: initialDuration,
    className,
    isOwn = false
}: AudioMessageProps) {
    const audioRef = useRef<HTMLAudioElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(initialDuration || 0);
    const [isLoaded, setIsLoaded] = useState(false);
    const [loadError, setLoadError] = useState(false);

    // Stable identity for the audio: the S3 key (or the blob URL) without the
    // presigned signature query string. Without this, every 8s refetch produces
    // a new signed URL → useEffect deps change → audio element reloads MID-PLAY
    // and the user hears "audio stops at half".
    const audioKey = (() => {
        if (!audioUrl) return "";
        if (audioUrl.startsWith("blob:")) return audioUrl;
        const m = audioUrl.match(/\/(audio-messages\/[^?]+)/);
        if (m) return m[1];
        // Fallback: strip query string
        const qIdx = audioUrl.indexOf("?");
        return qIdx >= 0 ? audioUrl.slice(0, qIdx) : audioUrl;
    })();

    // Keep a stable src — only update if the actual audio identity changes,
    // not when the presigned URL is refreshed by background refetches.
    const stableUrlRef = useRef<string>(audioUrl);
    const lastKeyRef = useRef<string>(audioKey);
    if (audioKey !== lastKeyRef.current) {
        lastKeyRef.current = audioKey;
        stableUrlRef.current = audioUrl;
    }
    const stableAudioUrl = stableUrlRef.current;

    // Generate waveform bars (visual representation)
    const waveformBars = useRef<number[]>(
        Array.from({ length: 30 }, () => Math.random() * 0.6 + 0.4)
    );

    // Animation ref for smooth progress
    const animationRef = useRef<number | null>(null);

    // Animation loop function
    const startAnimation = useCallback(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const updateProgress = () => {
            if (audio && !audio.paused) {
                setCurrentTime(audio.currentTime);
                // Also update duration if we don't have it yet
                if (audio.duration && isFinite(audio.duration)) {
                    setDuration(audio.duration);
                }
                animationRef.current = requestAnimationFrame(updateProgress);
            }
        };

        animationRef.current = requestAnimationFrame(updateProgress);
    }, []);

    const stopAnimation = useCallback(() => {
        if (animationRef.current) {
            cancelAnimationFrame(animationRef.current);
            animationRef.current = null;
        }
    }, []);

    // Setup audio event listeners
    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const handleLoadedMetadata = () => {
            if (audio.duration && isFinite(audio.duration)) {
                setDuration(audio.duration);
            }
            setIsLoaded(true);
        };

        const handleCanPlay = () => {
            if (audio.duration && isFinite(audio.duration)) {
                setDuration(audio.duration);
            }
            setIsLoaded(true);
        };

        const handlePlay = () => {
            setIsPlaying(true);
            startAnimation();
        };

        const handlePause = () => {
            setIsPlaying(false);
            stopAnimation();
        };

        const handleEnded = () => {
            setIsPlaying(false);
            setCurrentTime(0);
            stopAnimation();
        };

        const handleError = () => {
            setLoadError(true);
            setIsLoaded(false);
            stopAnimation();
        };

        // Reset error state when source changes
        setLoadError(false);
        setIsLoaded(false);

        audio.addEventListener('loadedmetadata', handleLoadedMetadata);
        audio.addEventListener('canplay', handleCanPlay);
        audio.addEventListener('play', handlePlay);
        audio.addEventListener('pause', handlePause);
        audio.addEventListener('ended', handleEnded);
        audio.addEventListener('error', handleError);

        // Force load
        audio.load();

        return () => {
            stopAnimation();
            audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
            audio.removeEventListener('canplay', handleCanPlay);
            audio.removeEventListener('play', handlePlay);
            audio.removeEventListener('pause', handlePause);
            audio.removeEventListener('ended', handleEnded);
            audio.removeEventListener('error', handleError);
        };
    }, [audioKey, startAnimation, stopAnimation]);

    const togglePlayPause = useCallback(async () => {
        const audio = audioRef.current;
        if (!audio) return;

        if (isPlaying) {
            audio.pause();
        } else {
            try {
                if (audio.readyState < 2) {
                    audio.load();
                    await new Promise<void>((resolve) => {
                        const onCanPlay = () => {
                            audio.removeEventListener('canplay', onCanPlay);
                            resolve();
                        };
                        audio.addEventListener('canplay', onCanPlay);
                    });
                }
                await audio.play();
            } catch (err) {
                console.error('[AudioMessage] Play failed:', err);
            }
        }
    }, [isPlaying]);

    const handleWaveformClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        const audio = audioRef.current;
        if (!audio || !duration) return;

        const rect = e.currentTarget.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const percentage = clickX / rect.width;
        audio.currentTime = percentage * duration;
        setCurrentTime(percentage * duration);
    }, [duration]);

    const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

    if (loadError) {
        return (
            <div className={cn('flex items-center gap-2 min-w-[180px] text-xs', className)}>
                <span className={cn('opacity-80', isOwn ? 'text-white' : 'text-gray-500')}>
                    ⚠️ Audio non disponibile
                </span>
            </div>
        );
    }

    return (
        <div className={cn('flex items-center gap-3 min-w-[180px]', className)}>
            {/* Hidden audio element */}
            <audio ref={audioRef} src={stableAudioUrl} preload="metadata" />

            {/* Play/Pause button */}
            <button
                onClick={togglePlayPause}
                disabled={!isLoaded}
                className={cn(
                    'flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-all',
                    isOwn
                        ? 'bg-white/20 text-white hover:bg-white/30'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300',
                    !isLoaded && 'opacity-50 cursor-not-allowed'
                )}
            >
                {isPlaying ? (
                    // Pause icon
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                        <path fillRule="evenodd" d="M6.75 5.25a.75.75 0 01.75-.75H9a.75.75 0 01.75.75v13.5a.75.75 0 01-.75.75H7.5a.75.75 0 01-.75-.75V5.25zm7.5 0A.75.75 0 0115 4.5h1.5a.75.75 0 01.75.75v13.5a.75.75 0 01-.75.75H15a.75.75 0 01-.75-.75V5.25z" clipRule="evenodd" />
                    </svg>
                ) : (
                    // Play icon
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 ml-0.5">
                        <path fillRule="evenodd" d="M4.5 5.653c0-1.426 1.529-2.33 2.779-1.643l11.54 6.348c1.295.712 1.295 2.573 0 3.285L7.28 19.991c-1.25.687-2.779-.217-2.779-1.643V5.653z" clipRule="evenodd" />
                    </svg>
                )}
            </button>

            <div className="flex-1 flex flex-col gap-1">
                {/* Waveform visualization with progress dot */}
                <div
                    className="relative flex items-center gap-[1px] h-6 cursor-pointer"
                    onClick={handleWaveformClick}
                >
                    {waveformBars.current.map((height, index) => {
                        const barProgress = ((index + 1) / waveformBars.current.length) * 100;
                        const isPast = barProgress <= progress;

                        return (
                            <div
                                key={index}
                                data-keep-white={(isOwn && isPast) || undefined}
                                className={cn(
                                    'w-[3px] rounded-full',
                                    isOwn
                                        ? isPast ? 'bg-white' : 'bg-white/40'
                                        : isPast ? 'bg-blue-500' : 'bg-gray-300'
                                )}
                                style={{ height: `${height * 100}%` }}
                            />
                        );
                    })}

                    {/* Progress dot indicator - positioned with the progress */}
                    <div
                        data-keep-white={isOwn || undefined}
                        className={cn(
                            'absolute w-3 h-3 rounded-full shadow-md -translate-y-1/2 top-1/2 z-10',
                            isOwn ? 'bg-white' : 'bg-blue-500'
                        )}
                        style={{
                            left: `${progress}%`,
                            transform: 'translate(-50%, -50%)',
                        }}
                    />
                </div>

                {/* Duration display */}
                <div className={cn(
                    'text-xs',
                    isOwn ? 'text-white/80' : 'text-gray-500'
                )}>
                    {isPlaying || currentTime > 0
                        ? formatDuration(currentTime)
                        : formatDuration(duration)}
                </div>
            </div>
        </div>
    );
}

export default AudioMessage;
